import { z } from 'zod';

/**
 * Tipos de operações no Op-Log
 * AIDEV-NOTE: Cada operação é registrada atomicamente com a operação principal
 */
export const operationTypeSchema = z.enum([
  'CREATE_PEDIDO',
  'UPDATE_PEDIDO',
  'DELETE_PEDIDO',
  'FINALIZAR_PEDIDO',
  'CREATE_VISITA',
  'UPDATE_VISITA',
  'CREATE_LEAD',
  'UPDATE_LEAD',
  'UPDATE_LEAD_STATUS',
  'CREATE_CLIENTE',
  'UPDATE_CLIENTE'
]);

export type OperationType = z.infer<typeof operationTypeSchema>;

/**
 * Status da operação no Op-Log
 */
export const operationStatusSchema = z.enum(['pending', 'syncing', 'synced', 'failed', 'conflict']);

export type OperationStatus = z.infer<typeof operationStatusSchema>;

/**
 * Payload criptografado de uma operação
 * AIDEV-NOTE: Payload é criptografado com AES-GCM antes de persistir
 */
export const encryptedPayloadSchema = z.object({
  iv: z.string(), // IV em base64
  data: z.string(), // Dados criptografados em base64
  tag: z.string() // Auth tag em base64
});

export type EncryptedPayload = z.infer<typeof encryptedPayloadSchema>;

/**
 * Schema de uma operação no Op-Log
 */
export const opLogSchema = z.object({
  id: z.string().uuid(),
  operation_type: operationTypeSchema,
  status: operationStatusSchema,
  entity_type: z.string(),
  entity_id: z.string().or(z.number().int()),
  encrypted_payload: encryptedPayloadSchema,
  retry_count: z.number().int().min(0).default(0),
  last_error: z.string().nullable(),
  created_at: z.number().int(), // Timestamp em ms
  synced_at: z.number().int().nullable(),
  priority: z.number().int().min(1).max(10).default(5)
});

export type OpLog = z.infer<typeof opLogSchema>;

/**
 * Schema para criar nova operação no Op-Log
 */
export const createOpLogSchema = z.object({
  operation_type: operationTypeSchema,
  entity_type: z.string(),
  entity_id: z.string().or(z.number().int()),
  payload: z.record(z.string(), z.unknown()), // Payload antes de criptografar
  priority: z.number().int().min(1).max(10).optional()
});

export type CreateOpLogInput = z.infer<typeof createOpLogSchema>;

/**
 * Resultado de sincronização
 */
export const syncResultSchema = z.object({
  success: z.boolean(),
  synced_count: z.number().int().min(0),
  failed_count: z.number().int().min(0),
  errors: z.array(
    z.object({
      id: z.string().uuid(),
      error: z.string()
    })
  )
});

export type SyncResult = z.infer<typeof syncResultSchema>;

/**
 * Configurações offline do cliente
 */
export const offlineConfigSchema = z.object({
  last_sync_at: z.number().int().nullable(),
  is_online: z.boolean(),
  grace_period_expires_at: z.number().int().nullable(),
  pending_operations_count: z.number().int().min(0)
});

export type OfflineConfig = z.infer<typeof offlineConfigSchema>;
